
import React from 'react';
import { FaceLivenessDetectorCore, AwsCredentialProvider } from '@aws-amplify/ui-react-liveness';
import { ThemeProvider } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { useDispatch } from 'react-redux'
import { livenessValidation } from '@/domains/auth/redux/authenticationReactive';

interface LivenessQuickStartReactInternalProps {
    livenessSessionId: string;
    awsSessionId: string;
    region: string;
    disableStartScreen?: boolean;
    onCameraAccessError?: () => void;
}

function LivenessQuickStartReact({credentials, livenessSessionId, awsSessionId, region, disableStartScreen, onCameraAccessError}: {credentials: any} & LivenessQuickStartReactInternalProps) {
    
    const dispatch = useDispatch();
    const credentialProvider: AwsCredentialProvider = async () => {
        // Get the credentials from the liveness-handler lambda
        return credentials
    }

    const handleAnalysisComplete: () => Promise<void> = async () => {
        dispatch(livenessValidation({ liveness: true, data: { session_id:awsSessionId, liveness_session_id: livenessSessionId } }));
    };

    const handleError = async (error: any) => {
        // Amplify's LivenessError wraps the real cause: `state` is the identifier
        // (e.g. SERVER_ERROR) and `error` is an Error carrying "the more specific
        // error name and stack trace" (per Amplify docs). The name is the reliably
        // populated field, so fold it into the message forwarded to persons-api.
        const underlyingError = error?.error ?? null;
        const errorState = error?.state ?? null;
        const errorStatus = errorState ?? error?.code ?? error?.name ?? null;
        const errorMessage = underlyingError
            ? [underlyingError.name, underlyingError.message].filter(Boolean).join(': ')
            : (error?.message ?? 'Liveness verification failed');

        // CAMERA_ACCESS_ERROR is user-fixable; skip Redux so LivenessWrapper does not redirect.
        if (errorState === 'CAMERA_ACCESS_ERROR' && onCameraAccessError) {
            onCameraAccessError();
            return;
        }
        dispatch(livenessValidation({
            liveness: false,
            data: {
                session_id: awsSessionId,
                liveness_session_id: livenessSessionId,
                error: true,
                error_status: errorStatus,
                error_message: errorMessage,
            },
        }));
    };

    const handleUserCancel = () => {
        dispatch(livenessValidation({
            liveness: false,
            data: {
                session_id: awsSessionId,
                liveness_session_id: livenessSessionId,
                error: true,
                error_status: 'USER_CANCELLED',
                error_message: 'The user cancelled AWS liveness verification.',
            },
        }));
    };

    return (
        <ThemeProvider>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
                <FaceLivenessDetectorCore
                    sessionId={awsSessionId}
                    region={region}
                    onAnalysisComplete={handleAnalysisComplete}
                    onError={handleError}
                    onUserCancel={handleUserCancel}
                    config={{ credentialProvider }}
                    disableStartScreen={disableStartScreen}
                />
            </div>
        </ThemeProvider>
    );
}

export default LivenessQuickStartReact;
